import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Send,
  FileText,
  FileCheck,
  Plus,
  Upload,
  CheckCircle,
  Settings2,
  Mail,
  Star,
  Clock,
  Type,
  Trash2,
  Eye,
} from 'lucide-react';
import { TemplateFieldEditor } from '../components/TemplateFieldEditor';
import { toast } from 'sonner';

export default function SendMSA() {
  const { api } = useAuth();
  const [activeTab, setActiveTab] = useState('msa');
  const [msaTemplates, setMsaTemplates] = useState([]);
  const [poTemplates, setPoTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  
  // Send form state
  const [vendorEmail, setVendorEmail] = useState('');
  const [vendorName, setVendorName] = useState('');
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  
  // Create Template modal state
  const [templateModalOpen, setTemplateModalOpen] = useState(false);
  const [templateType, setTemplateType] = useState('msa'); // 'msa' or 'po'
  const [contentMode, setContentMode] = useState('upload'); // 'upload' or 'text'
  const [templateName, setTemplateName] = useState('');
  const [templateDescription, setTemplateDescription] = useState('');
  const [templateContent, setTemplateContent] = useState('');
  const [templateFile, setTemplateFile] = useState(null);
  const [creatingTemplate, setCreatingTemplate] = useState(false);
  
  // Field Editor state
  const [fieldEditorOpen, setFieldEditorOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templateFields, setTemplateFields] = useState([]);
  const [fieldEditorType, setFieldEditorType] = useState('msa');
  
  // View Template modal
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [viewingTemplate, setViewingTemplate] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [msaRes, poRes] = await Promise.all([
        api().get('/msa/templates'),
        api().get('/po/templates')
      ]);
      setMsaTemplates(msaRes.data);
      setPoTemplates(poRes.data);
      
      // Set default template
      if (msaRes.data.length > 0) {
        const master = msaRes.data.find(t => t.is_master_template);
        setSelectedTemplateId(master?.id || msaRes.data[0].id);
      }
    } catch (error) {
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 10MB');
      return;
    }
    
    const reader = new FileReader();
    reader.onload = () => {
      setTemplateFile({
        data: reader.result,
        name: file.name,
        type: file.type
      });
      toast.success('File loaded');
    };
    reader.readAsDataURL(file);
  };

  const openCreateModal = (type) => {
    setTemplateType(type);
    setContentMode('upload');
    setTemplateName('');
    setTemplateDescription('');
    setTemplateContent('');
    setTemplateFile(null);
    setTemplateModalOpen(true);
  };

  const handleCreateTemplate = async (e) => {
    e.preventDefault();
    
    if (contentMode === 'upload' && !templateFile) {
      toast.error('Please upload a document');
      return;
    }
    if (contentMode === 'text' && !templateContent.trim()) {
      toast.error('Please enter template content');
      return;
    }
    
    setCreatingTemplate(true);
    try {
      const endpoint = templateType === 'msa' ? '/msa/templates' : '/po/templates';
      const payload = {
        name: templateName,
        description: templateDescription,
        content: contentMode === 'text' ? templateContent : 'See attached document',
        is_master_template: true
      };
      
      if (contentMode === 'upload' && templateFile) {
        payload.file_data = templateFile.data;
        payload.file_name = templateFile.name;
        payload.file_type = templateFile.type;
      }
      
      const response = await api().post(endpoint, payload);
      
      toast.success(`${templateType.toUpperCase()} Template created!`);
      setTemplateModalOpen(false);
      
      // If file uploaded, open field editor
      if (contentMode === 'upload' && templateFile) {
        setSelectedTemplate(response.data);
        setTemplateFields([]);
        setFieldEditorType(templateType);
        setFieldEditorOpen(true);
        toast.info('Now map the signature fields on your document');
      }
      
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create template');
    } finally {
      setCreatingTemplate(false);
    }
  };

  const handleSend = async (e, type) => {
    e.preventDefault();
    if (!vendorEmail || !selectedTemplateId) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setSending(true);
    try {
      const endpoint = type === 'msa' ? '/msa/send-to-vendor' : '/po/send-to-vendor';
      await api().post(endpoint, {
        template_id: selectedTemplateId,
        vendor_email: vendorEmail,
        vendor_name: vendorName || null
      });
      
      toast.success(`${type.toUpperCase()} sent to ${vendorEmail}!`);
      setVendorEmail('');
      setVendorName('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to send ${type.toUpperCase()}`);
    } finally {
      setSending(false);
    }
  };

  const openFieldEditor = async (template, type) => {
    if (!template.has_file) {
      toast.error('Field mapping requires a document file. This template only has text content.');
      return;
    }
    
    setSelectedTemplate(template);
    setFieldEditorType(type);
    try {
      const endpoint = type === 'msa' 
        ? `/msa/templates/${template.id}/fields`
        : `/po/templates/${template.id}/fields`;
      const response = await api().get(endpoint);
      setTemplateFields(response.data);
    } catch (error) {
      setTemplateFields([]);
    }
    setFieldEditorOpen(true);
  };

  const handleSaveFields = async (fields) => {
    const fieldsToSave = fields.map(f => ({
      field_type: f.field_type,
      field_name: f.field_name,
      role: f.role,
      position_x: f.position_x,
      position_y: f.position_y,
      page_number: f.page_number,
      width: f.width,
      height: f.height,
    }));
    
    const endpoint = fieldEditorType === 'msa'
      ? `/msa/templates/${selectedTemplate.id}/fields/bulk`
      : `/po/templates/${selectedTemplate.id}/fields/bulk`;
    
    await api().post(endpoint, fieldsToSave);
    setFieldEditorOpen(false);
    fetchData();
  };

  const handleDeleteTemplate = async (templateId, type) => {
    if (!confirm('Are you sure you want to delete this template?')) return;
    
    try {
      const endpoint = type === 'msa' 
        ? `/msa/templates/${templateId}`
        : `/po/templates/${templateId}`;
      await api().delete(endpoint);
      toast.success('Template deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete template');
    }
  };

  const viewTemplate = (template) => {
    setViewingTemplate(template);
    setViewModalOpen(true);
  };

  const currentTemplates = activeTab === 'msa' ? msaTemplates : poTemplates;

  return (
    <div className="min-h-screen bg-zinc-950 p-6" data-testid="create-documents-page">
      <div className="max-w-6xl mx-auto">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-white mb-2">Create & Send Documents</h1>
          <p className="text-zinc-400">Create MSA and PO templates, map fields, and send to vendors</p>
        </div>

        <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); setSelectedTemplateId(''); }}>
          <TabsList className="bg-zinc-900 border border-zinc-800 mb-6">
            <TabsTrigger value="msa" className="data-[state=active]:bg-blue-600">
              <FileText className="w-4 h-4 mr-2" />
              MSA
            </TabsTrigger>
            <TabsTrigger value="po" className="data-[state=active]:bg-emerald-600">
              <FileCheck className="w-4 h-4 mr-2" />
              PO
            </TabsTrigger>
          </TabsList>

          {/* MSA Tab */}
          <TabsContent value="msa">
            <div className="space-y-6">
              {/* Action Buttons */}
              <div className="flex gap-4">
                <Button
                  onClick={() => openCreateModal('msa')}
                  className="bg-blue-600 hover:bg-blue-500"
                  data-testid="create-msa-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create MSA Template
                </Button>
              </div>

              {/* Templates Grid */}
              <div>
                <h2 className="text-lg font-semibold text-white mb-4">MSA Templates</h2>
                {msaTemplates.length === 0 ? (
                  <Card className="bg-zinc-900/50 border-zinc-800">
                    <CardContent className="py-12 text-center">
                      <FileText className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-white mb-2">No MSA Templates</h3>
                      <p className="text-zinc-400 mb-4">Create your first MSA template to get started</p>
                      <Button onClick={() => openCreateModal('msa')} className="bg-blue-600 hover:bg-blue-500">
                        <Plus className="w-4 h-4 mr-2" />
                        Create MSA Template
                      </Button>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {msaTemplates.map(template => (
                      <TemplateCard
                        key={template.id}
                        template={template}
                        type="msa"
                        onMapFields={() => openFieldEditor(template, 'msa')}
                        onView={() => viewTemplate(template)}
                        onDelete={() => handleDeleteTemplate(template.id, 'msa')}
                        onSelect={() => setSelectedTemplateId(template.id)}
                        isSelected={selectedTemplateId === template.id}
                      />
                    ))}
                  </div>
                )}
              </div>

              {/* Send MSA Form */}
              {msaTemplates.length > 0 && (
                <Card className="bg-zinc-900/50 border-zinc-800">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                      <Send className="w-5 h-5 text-blue-400" />
                      Send MSA to Vendor
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={(e) => handleSend(e, 'msa')} className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="space-y-2">
                          <Label className="text-zinc-300">Template *</Label>
                          <Select value={selectedTemplateId} onValueChange={setSelectedTemplateId}>
                            <SelectTrigger className="bg-zinc-950 border-zinc-800">
                              <SelectValue placeholder="Select template" />
                            </SelectTrigger>
                            <SelectContent className="bg-zinc-900 border-zinc-800">
                              {msaTemplates.map(t => (
                                <SelectItem key={t.id} value={t.id}>
                                  {t.name} {t.is_master_template && '⭐'}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label className="text-zinc-300">Vendor Email *</Label>
                          <Input
                            type="email"
                            value={vendorEmail}
                            onChange={(e) => setVendorEmail(e.target.value)}
                            placeholder="vendor@company.com"
                            className="bg-zinc-950 border-zinc-800"
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-zinc-300">Vendor Name</Label>
                          <Input
                            type="text"
                            value={vendorName}
                            onChange={(e) => setVendorName(e.target.value)}
                            placeholder="Optional"
                            className="bg-zinc-950 border-zinc-800"
                          />
                        </div>
                      </div>
                      <Button
                        type="submit"
                        className="bg-blue-600 hover:bg-blue-500"
                        disabled={sending || !selectedTemplateId || !vendorEmail}
                      >
                        {sending ? 'Sending...' : 'Send MSA'}
                        <Send className="w-4 h-4 ml-2" />
                      </Button>
                    </form>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* PO Tab */}
          <TabsContent value="po">
            <div className="space-y-6">
              {/* Action Buttons */}
              <div className="flex gap-4">
                <Button
                  onClick={() => openCreateModal('po')}
                  className="bg-emerald-600 hover:bg-emerald-500"
                  data-testid="create-po-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create PO Template
                </Button>
              </div>

              {/* Templates Grid */}
              <div>
                <h2 className="text-lg font-semibold text-white mb-4">PO Templates</h2>
                {poTemplates.length === 0 ? (
                  <Card className="bg-zinc-900/50 border-zinc-800">
                    <CardContent className="py-12 text-center">
                      <FileCheck className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-white mb-2">No PO Templates</h3>
                      <p className="text-zinc-400 mb-4">Create your first PO template to get started</p>
                      <Button onClick={() => openCreateModal('po')} className="bg-emerald-600 hover:bg-emerald-500">
                        <Plus className="w-4 h-4 mr-2" />
                        Create PO Template
                      </Button>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {poTemplates.map(template => (
                      <TemplateCard
                        key={template.id}
                        template={template}
                        type="po"
                        onMapFields={() => openFieldEditor(template, 'po')}
                        onView={() => viewTemplate(template)}
                        onDelete={() => handleDeleteTemplate(template.id, 'po')}
                        onSelect={() => setSelectedTemplateId(template.id)}
                        isSelected={selectedTemplateId === template.id}
                      />
                    ))}
                  </div>
                )}
              </div>

              {/* Send PO Form */}
              {poTemplates.length > 0 && (
                <Card className="bg-zinc-900/50 border-zinc-800">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                      <Send className="w-5 h-5 text-emerald-400" />
                      Send PO to Vendor
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={(e) => handleSend(e, 'po')} className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="space-y-2">
                          <Label className="text-zinc-300">Template *</Label>
                          <Select value={selectedTemplateId} onValueChange={setSelectedTemplateId}>
                            <SelectTrigger className="bg-zinc-950 border-zinc-800">
                              <SelectValue placeholder="Select template" />
                            </SelectTrigger>
                            <SelectContent className="bg-zinc-900 border-zinc-800">
                              {poTemplates.map(t => (
                                <SelectItem key={t.id} value={t.id}>
                                  {t.name} {t.is_master_template && '⭐'}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label className="text-zinc-300">Vendor Email *</Label>
                          <Input
                            type="email"
                            value={vendorEmail}
                            onChange={(e) => setVendorEmail(e.target.value)}
                            placeholder="vendor@company.com"
                            className="bg-zinc-950 border-zinc-800"
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-zinc-300">Vendor Name</Label>
                          <Input
                            type="text"
                            value={vendorName}
                            onChange={(e) => setVendorName(e.target.value)}
                            placeholder="Optional"
                            className="bg-zinc-950 border-zinc-800"
                          />
                        </div>
                      </div>
                      <Button
                        type="submit"
                        className="bg-emerald-600 hover:bg-emerald-500"
                        disabled={sending || !selectedTemplateId || !vendorEmail}
                      >
                        {sending ? 'Sending...' : 'Send PO'}
                        <Send className="w-4 h-4 ml-2" />
                      </Button>
                    </form>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Create Template Modal */}
      <Dialog open={templateModalOpen} onOpenChange={setTemplateModalOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              {templateType === 'msa' ? (
                <FileText className="w-5 h-5 text-blue-400" />
              ) : (
                <FileCheck className="w-5 h-5 text-emerald-400" />
              )}
              Create {templateType.toUpperCase()} Template
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              Upload a document or type your template content
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleCreateTemplate} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-zinc-300">Template Name *</Label>
              <Input
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                placeholder={`Master ${templateType.toUpperCase()} Template`}
                className="bg-zinc-950 border-zinc-800"
                required
              />
            </div>
            
            <div className="space-y-2">
              <Label className="text-zinc-300">Description</Label>
              <Input
                value={templateDescription}
                onChange={(e) => setTemplateDescription(e.target.value)}
                placeholder="Brief description of this template"
                className="bg-zinc-950 border-zinc-800"
              />
            </div>
            
            {/* Content Mode Toggle */}
            <div className="space-y-2">
              <Label className="text-zinc-300">Content Type</Label>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant={contentMode === 'upload' ? 'default' : 'outline'}
                  onClick={() => setContentMode('upload')}
                  className={contentMode === 'upload' 
                    ? 'bg-blue-600 hover:bg-blue-500' 
                    : 'border-zinc-700'}
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Upload Document
                </Button>
                <Button
                  type="button"
                  variant={contentMode === 'text' ? 'default' : 'outline'}
                  onClick={() => setContentMode('text')}
                  className={contentMode === 'text' 
                    ? 'bg-blue-600 hover:bg-blue-500' 
                    : 'border-zinc-700'}
                >
                  <Type className="w-4 h-4 mr-2" />
                  Type Content
                </Button>
              </div>
            </div>
            
            {/* Upload Mode */}
            {contentMode === 'upload' && (
              <div className="space-y-2">
                <Label className="text-zinc-300">Upload Document *</Label>
                <div className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                  templateFile ? 'border-emerald-500/50 bg-emerald-500/5' : 'border-zinc-700 hover:border-zinc-600'
                }`}>
                  {templateFile ? (
                    <div className="flex items-center justify-center gap-2">
                      <CheckCircle className="w-5 h-5 text-emerald-400" />
                      <span className="text-emerald-400">{templateFile.name}</span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setTemplateFile(null)}
                        className="text-zinc-400 hover:text-red-400"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ) : (
                    <>
                      <Upload className="w-8 h-8 text-zinc-500 mx-auto mb-2" />
                      <p className="text-sm text-zinc-400">Click to upload or drag and drop</p>
                    </>
                  )}
                  <Input
                    type="file"
                    accept=".pdf,.doc,.docx,.txt"
                    onChange={handleFileUpload}
                    className="bg-zinc-950 border-zinc-800 max-w-xs mx-auto mt-2"
                  />
                  <p className="text-xs text-zinc-500 mt-2">PDF, Word, or Text (max 10MB)</p>
                </div>
                <p className="text-xs text-amber-400">
                  After creating, you can map signature fields on the document
                </p>
              </div>
            )}
            
            {/* Text Mode */}
            {contentMode === 'text' && (
              <div className="space-y-2">
                <Label className="text-zinc-300">Template Content *</Label>
                <Textarea
                  value={templateContent}
                  onChange={(e) => setTemplateContent(e.target.value)}
                  placeholder={`Enter your ${templateType.toUpperCase()} template content here...`}
                  className="bg-zinc-950 border-zinc-800 min-h-[200px] font-mono text-sm"
                  required={contentMode === 'text'}
                />
                <p className="text-xs text-zinc-500">
                  You can use placeholders like {'{vendor_name}'}, {'{date}'}, etc.
                </p>
              </div>
            )}
            
            <div className="flex justify-end gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setTemplateModalOpen(false)}
                className="border-zinc-700"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className={templateType === 'msa' ? 'bg-blue-600 hover:bg-blue-500' : 'bg-emerald-600 hover:bg-emerald-500'}
                disabled={creatingTemplate || !templateName || (contentMode === 'upload' && !templateFile) || (contentMode === 'text' && !templateContent.trim())}
              >
                {creatingTemplate ? 'Creating...' : contentMode === 'upload' ? 'Create & Map Fields' : 'Create Template'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Field Editor Dialog */}
      <Dialog open={fieldEditorOpen} onOpenChange={setFieldEditorOpen}>
        <DialogContent className="bg-zinc-950 border-zinc-800 max-w-[95vw] h-[90vh] p-0">
          {selectedTemplate && (
            <TemplateFieldEditor
              templateId={selectedTemplate.id}
              fileName={selectedTemplate.file_name || selectedTemplate.name}
              existingFields={templateFields}
              onSave={handleSaveFields}
              onClose={() => setFieldEditorOpen(false)}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* View Template Modal */}
      <Dialog open={viewModalOpen} onOpenChange={setViewModalOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white">{viewingTemplate?.name}</DialogTitle>
            <DialogDescription className="text-zinc-400">
              {viewingTemplate?.description || 'No description'}
            </DialogDescription>
          </DialogHeader>
          <div className="bg-white rounded-lg p-4 text-black">
            <pre className="whitespace-pre-wrap font-sans text-sm">
              {viewingTemplate?.content || 'No content'}
            </pre>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Template Card Component
function TemplateCard({ template, type, onMapFields, onView, onDelete, onSelect, isSelected }) {
  const iconColor = type === 'msa' ? 'text-blue-400' : 'text-emerald-400';
  const Icon = type === 'msa' ? FileText : FileCheck;
  
  return (
    <Card className={`bg-zinc-900/50 border-zinc-800 hover:border-zinc-700 transition-colors ${
      isSelected ? 'ring-2 ring-blue-500/50' : ''
    } ${template.is_master_template ? 'ring-1 ring-amber-500/30' : ''}`}>
      <CardContent className="pt-6">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2">
            <Icon className={`w-5 h-5 ${iconColor}`} />
            <h3 className="font-medium text-white truncate max-w-[150px]">{template.name}</h3>
          </div>
          {template.is_master_template && (
            <Badge className="bg-amber-500/10 text-amber-400 border border-amber-500/20 text-[10px]">
              <Star className="w-3 h-3 mr-1 fill-amber-400" />
              Master
            </Badge>
          )}
        </div>
        
        <p className="text-xs text-zinc-400 mb-3 line-clamp-2 h-8">
          {template.description || 'No description'}
        </p>
        
        <div className="flex items-center gap-3 text-xs text-zinc-500 mb-4">
          {template.has_file ? (
            <span className="flex items-center gap-1">
              <Upload className="w-3 h-3" />
              File
            </span>
          ) : (
            <span className="flex items-center gap-1">
              <Type className="w-3 h-3" />
              Text
            </span>
          )}
          <span className="flex items-center gap-1">
            <Settings2 className="w-3 h-3" />
            {template.fields_count || 0} Fields
          </span>
        </div>
        
        <div className="flex gap-2">
          {template.has_file && (
            <Button
              variant="outline"
              size="sm"
              onClick={onMapFields}
              className="flex-1 border-zinc-700 hover:bg-zinc-800 text-xs"
            >
              <Settings2 className="w-3 h-3 mr-1" />
              Map Fields
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={onView}
            className="border-zinc-700 hover:bg-zinc-800"
          >
            <Eye className="w-3 h-3" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={onDelete}
            className="border-zinc-700 hover:bg-red-900/20 text-red-400"
          >
            <Trash2 className="w-3 h-3" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
